import type {
  AssetRateTransport,
  LadderTierTransport,
  LatestNotificationTransport,
  MaxRebatePerLotTransport,
  RebateDetailsTransport,
  RebateTierLadderTransport,
  RebateTierTransport,
  VolumeUnitsTransport,
} from "./transport";
import type { Region } from "./types";

export const FIXTURE_EVALUATED_AT = "2026-08-14T09:00:00.000Z";

export const isoOffsetDays = (days: number): string =>
  new Date(Date.now() + days * 86_400_000).toISOString();

export const rebateTierFixtures: Record<Region, RebateTierTransport> = {
  TH: {
    partnerTierID: "tier-th-001",
    region: "APAC_TH",
    tierName: "Silver",
    tierRank: 3,
    qualifiedTierName: "Silver",
    qualifiedTierRank: 3,
    tierStatus: "Upgrading",
    earning: 12_500,
    tradeVolumeUSD: 120_000_000,
    evaluatedAt: FIXTURE_EVALUATED_AT,
    defaultVolumeUnit: null,
    freezePeriodValidTo: null,
    gracePeriodValidTo: null,
    isExclusive: false,
    isTierWhitelisted: true,
    isTierVisible: true,
    isOnboardingGrace: false,
    activeClients: 6,
    criteriaProgress: [
      {
        type: "VolumeUSD",
        months: [
          { month: "2026-06", value: 38_000_000 },
          { month: "2026-07", value: 40_000_000 },
          { month: "2026-08", value: 42_000_000 },
        ],
      },
      {
        type: "ActiveClients",
        months: [
          { month: "2026-06", value: 5 },
          { month: "2026-07", value: 6 },
          { month: "2026-08", value: 6 },
        ],
      },
    ],
  },
  VN: {
    partnerTierID: "tier-vn-001",
    region: "APAC_VN",
    tierName: "Silver",
    tierRank: 3,
    qualifiedTierName: "Silver",
    qualifiedTierRank: 3,
    tierStatus: "Upgrading",
    earning: 8_200,
    tradeVolumeUSD: 60_000_000,
    evaluatedAt: FIXTURE_EVALUATED_AT,
    defaultVolumeUnit: null,
    freezePeriodValidTo: null,
    gracePeriodValidTo: null,
    isExclusive: false,
    isTierWhitelisted: true,
    isTierVisible: true,
    isOnboardingGrace: false,
    activeClients: null,
    criteriaProgress: [
      {
        type: "VolumeUSD",
        months: [
          { month: "2026-06", value: 18_000_000 },
          { month: "2026-07", value: 20_000_000 },
          { month: "2026-08", value: 22_000_000 },
        ],
      },
    ],
  },
  HK: {
    partnerTierID: "tier-hk-001",
    region: "APAC_HK",
    tierName: "Silver",
    tierRank: 2,
    qualifiedTierName: "Silver",
    qualifiedTierRank: 2,
    tierStatus: "Maintaining",
    earning: 21_000,
    tradeVolumeUSD: 60_000_000,
    evaluatedAt: FIXTURE_EVALUATED_AT,
    defaultVolumeUnit: null,
    freezePeriodValidTo: null,
    gracePeriodValidTo: null,
    isExclusive: false,
    isTierWhitelisted: true,
    isTierVisible: true,
    isOnboardingGrace: false,
    activeClients: 5,
    criteriaProgress: [
      {
        type: "VolumeUSD",
        months: [
          { month: "2026-06", value: 18_000_000 },
          { month: "2026-07", value: 20_000_000 },
          { month: "2026-08", value: 22_000_000 },
        ],
      },
      {
        type: "ActiveClients",
        months: [
          { month: "2026-06", value: 4 },
          { month: "2026-07", value: 5 },
          { month: "2026-08", value: 5 },
        ],
      },
    ],
  },
  REST: {
    partnerTierID: "tier-rest-001",
    region: "APAC_REST",
    tierName: "Silver",
    tierRank: 2,
    qualifiedTierName: "Gold",
    qualifiedTierRank: 3,
    tierStatus: "Upgrading",
    earning: 15_300,
    tradeVolumeUSD: 70_000_000,
    evaluatedAt: FIXTURE_EVALUATED_AT,
    defaultVolumeUnit: null,
    freezePeriodValidTo: null,
    gracePeriodValidTo: null,
    isExclusive: false,
    isTierWhitelisted: true,
    isTierVisible: true,
    isOnboardingGrace: false,
    activeClients: null,
    criteriaProgress: [
      {
        type: "VolumeUSD",
        months: [
          { month: "2026-06", value: 22_000_000 },
          { month: "2026-07", value: 23_000_000 },
          { month: "2026-08", value: 25_000_000 },
        ],
      },
    ],
  },
  LATAM: {
    partnerTierID: "tier-latam-001",
    region: "LATAM",
    tierName: "Silver",
    tierRank: 4,
    qualifiedTierName: "Silver",
    qualifiedTierRank: 4,
    tierStatus: "Upgrading",
    earning: 33_400,
    tradeVolumeUSD: 300_000_000,
    evaluatedAt: FIXTURE_EVALUATED_AT,
    defaultVolumeUnit: null,
    freezePeriodValidTo: null,
    gracePeriodValidTo: null,
    isExclusive: false,
    isTierWhitelisted: true,
    isTierVisible: true,
    isOnboardingGrace: false,
    activeClients: 10,
    criteriaProgress: [
      {
        type: "VolumeUSD",
        months: [
          { month: "2026-06", value: 95_000_000 },
          { month: "2026-07", value: 100_000_000 },
          { month: "2026-08", value: 105_000_000 },
        ],
      },
      {
        type: "ActiveClients",
        months: [
          { month: "2026-06", value: 8 },
          { month: "2026-07", value: 9 },
          { month: "2026-08", value: 10 },
        ],
      },
      {
        type: "NewClients",
        months: [
          { month: "2026-06", value: 3 },
          { month: "2026-07", value: 2 },
          { month: "2026-08", value: 3 },
        ],
      },
    ],
  },
  MENA: {
    partnerTierID: "tier-mena-001",
    region: "MENA_EEA_AU",
    tierName: "Silver",
    tierRank: 2,
    qualifiedTierName: "Gold",
    qualifiedTierRank: 3,
    tierStatus: "Upgrading",
    earning: 9_900,
    tradeVolumeUSD: 120_000_000,
    evaluatedAt: FIXTURE_EVALUATED_AT,
    defaultVolumeUnit: null,
    freezePeriodValidTo: null,
    gracePeriodValidTo: null,
    isExclusive: false,
    isTierWhitelisted: true,
    isTierVisible: true,
    isOnboardingGrace: false,
    activeClients: null,
    criteriaProgress: [
      {
        type: "VolumeUSD",
        months: [
          { month: "2026-06", value: 38_000_000 },
          { month: "2026-07", value: 40_000_000 },
          { month: "2026-08", value: 42_000_000 },
        ],
      },
    ],
  },
};

export const defaultRebateTierFixture: RebateTierTransport =
  rebateTierFixtures.REST;

export const fraudulentRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-fraud",
  tierStatus: "Fraudulent",
};

export const freezeFixtureExpiringIn = (days: number): RebateTierTransport => ({
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-freeze",
  tierStatus: "Maintaining",
  freezePeriodValidTo: isoOffsetDays(days),
});

export const frozenRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-frozen",
  tierStatus: "Tier frozen",
  freezePeriodValidTo: isoOffsetDays(30),
};

export const freezeActiveRebateTierFixture: RebateTierTransport =
  freezeFixtureExpiringIn(30);

export const freezeLapsedRebateTierFixture: RebateTierTransport =
  freezeFixtureExpiringIn(-1);

export const tierDowngradeRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-downgrade",
  tierStatus: "Maintaining",
  qualifiedTierName: "Bronze",
  qualifiedTierRank: 1,
};

export const tierUpgradeRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-upgrade",
  tierStatus: "Maintaining",
  qualifiedTierName: "Gold",
  qualifiedTierRank: 3,
};

export const longVolumeRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.HK,
  partnerTierID: "tier-hk-long-volume",
  tierRank: 5,
  qualifiedTierName: "Brilliant",
  qualifiedTierRank: 5,
  tradeVolumeUSD: 999_999_000_000,
};

export const graceRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-grace",
  tierStatus: "Maintaining",
  qualifiedTierName: "Silver",
  qualifiedTierRank: 2,
  gracePeriodValidTo: isoOffsetDays(90),
};

export const maintainingRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-maintaining",
  tierStatus: "Maintaining",
  qualifiedTierName: "Silver",
  qualifiedTierRank: 2,
};

export const finalTierRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.LATAM,
  partnerTierID: "tier-latam-final",
  tierName: "Platinum",
  tierRank: 6,
  qualifiedTierName: "Platinum",
  qualifiedTierRank: 6,
  tierStatus: "Maintaining",
};

export const finalTierUpgradingRebateTierFixture: RebateTierTransport = {
  ...finalTierRebateTierFixture,
  partnerTierID: "tier-latam-final-upgrading",
  tierStatus: "Upgrading",
};

export const tierHiddenRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-hidden",
  isTierVisible: false,
};

export const qualificationCountdownRebateTierFixture: RebateTierTransport = {
  ...maintainingRebateTierFixture,
  partnerTierID: "tier-rest-qualification-countdown",
};

export const QUALIFICATION_COUNTDOWN_CYCLE_END = "2026-09-01T00:00:00.000Z";

export const qualificationCountdownNow = (daysBeforeCycleEnd: number): Date =>
  new Date(
    Date.parse(QUALIFICATION_COUNTDOWN_CYCLE_END) -
      daysBeforeCycleEnd * 86_400_000,
  );

export const QUALIFICATION_COUNTDOWN_NOW = qualificationCountdownNow(9);

export const eventNotificationRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-boosted",
  tierStatus: "Maintaining",
  qualifiedTierName: "Silver",
  qualifiedTierRank: 2,
  isOnboardingGrace: true,
  gracePeriodValidTo: isoOffsetDays(90),
};

export const graceWithTierEventRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-grace-with-tier-event",
  tierStatus: "Maintaining",
  qualifiedTierName: "Gold",
  qualifiedTierRank: 3,
  gracePeriodValidTo: isoOffsetDays(90),
};

export const noProgressRebateTierFixture: RebateTierTransport = {
  ...rebateTierFixtures.REST,
  partnerTierID: "tier-rest-no-progress",
  tradeVolumeUSD: 0,
  evaluatedAt: undefined,
  criteriaProgress: null,
};

export type RebateTierScenario =
  | "default"
  | "maintaining"
  | "qualificationCountdown"
  | "grace"
  | "frozen"
  | "fraudulent"
  | "tierHidden"
  | "eventNotification"
  | "noProgress"
  | "freezeActive"
  | "freezeLapsed"
  | "tierUpgrade"
  | "tierDowngrade"
  | "longVolume"
  | "fiveTier"
  | "sixTier"
  | "finalTier"
  | "finalTierUpgrading"
  | "graceWithTierEvent";

export const rebateTierScenarioFixtures: Record<
  RebateTierScenario,
  RebateTierTransport
> = {
  default: defaultRebateTierFixture,
  maintaining: maintainingRebateTierFixture,
  qualificationCountdown: qualificationCountdownRebateTierFixture,
  grace: graceRebateTierFixture,
  frozen: frozenRebateTierFixture,
  fraudulent: fraudulentRebateTierFixture,
  tierHidden: tierHiddenRebateTierFixture,
  eventNotification: eventNotificationRebateTierFixture,
  noProgress: noProgressRebateTierFixture,
  freezeActive: freezeActiveRebateTierFixture,
  freezeLapsed: freezeLapsedRebateTierFixture,
  tierUpgrade: tierUpgradeRebateTierFixture,
  tierDowngrade: tierDowngradeRebateTierFixture,
  longVolume: longVolumeRebateTierFixture,
  fiveTier: rebateTierFixtures.TH,
  sixTier: rebateTierFixtures.HK,
  finalTier: finalTierRebateTierFixture,
  finalTierUpgrading: finalTierUpgradingRebateTierFixture,
  graceWithTierEvent: graceWithTierEventRebateTierFixture,
};

/** Fixture authoring shape: `defaultSetId` is mapped to the wire's `rebateDefaultSetId`. */
type FixtureLadderTier = Omit<LadderTierTransport, "rebateDefaultSetId"> & {
  defaultSetId?: number;
  rebatePerLot: number;
};

const FIXTURE_LADDERS: Record<
  Region,
  { criteria: string[]; tiers: FixtureLadderTier[] }
> = {
  TH: {
    criteria: ["tv", "ac"],
    tiers: [
      {
        key: "bronze",
        rank: 2,
        tv: 0,
        ac: 0,
        defaultSetId: 1102,
        rebatePerLot: 2,
      },
      {
        key: "silver",
        rank: 3,
        tv: 96_000_000,
        ac: 5,
        defaultSetId: 1103,
        rebatePerLot: 3,
      },
      {
        key: "gold",
        rank: 4,
        tv: 160_000_000,
        ac: 10,
        defaultSetId: 1104,
        rebatePerLot: 5,
      },
      {
        key: "platinum",
        rank: 5,
        tv: 220_000_000,
        ac: 15,
        defaultSetId: 1105,
        rebatePerLot: 7,
      },
      {
        key: "elite",
        rank: 6,
        tv: 650_000_000,
        ac: 15,
        defaultSetId: 1106,
        rebatePerLot: 10,
      },
    ],
  },
  VN: {
    criteria: ["tv"],
    tiers: [
      { key: "bronze", rank: 2, tv: 0, defaultSetId: 1202, rebatePerLot: 2 },
      {
        key: "silver",
        rank: 3,
        tv: 20_700_000,
        defaultSetId: 1203,
        rebatePerLot: 3,
      },
      {
        key: "gold",
        rank: 4,
        tv: 103_500_000,
        defaultSetId: 1204,
        rebatePerLot: 5,
      },
      {
        key: "platinum",
        rank: 5,
        tv: 207_000_000,
        defaultSetId: 1205,
        rebatePerLot: 7,
      },
      {
        key: "elite",
        rank: 6,
        tv: 414_000_000,
        defaultSetId: 1206,
        rebatePerLot: 10,
      },
    ],
  },
  HK: {
    criteria: ["tv", "ac"],
    tiers: [
      { key: "bronze", rank: 1, tv: 0, defaultSetId: 1301, rebatePerLot: 1 },
      {
        key: "silver",
        rank: 2,
        tv: 44_850_000,
        ac: 4,
        defaultSetId: 1302,
        rebatePerLot: 2,
      },
      {
        key: "gold",
        rank: 3,
        tv: 89_700_000,
        ac: 11,
        defaultSetId: 1303,
        rebatePerLot: 3,
      },
      {
        key: "platinum",
        rank: 4,
        tv: 448_500_000,
        ac: 31,
        defaultSetId: 1304,
        rebatePerLot: 5,
      },
      {
        key: "brilliant",
        rank: 5,
        tv: 897_000_000,
        ac: 81,
        defaultSetId: 1305,
        rebatePerLot: 7,
      },
      {
        key: "elite",
        rank: 6,
        tv: 1_794_000_000,
        ac: 201,
        defaultSetId: 1306,
        rebatePerLot: 10,
      },
    ],
  },
  REST: {
    criteria: ["tv"],
    tiers: [
      { key: "bronze", rank: 1, tv: 0, defaultSetId: 1401, rebatePerLot: 1 },
      {
        key: "silver",
        rank: 2,
        tv: 44_850_000,
        defaultSetId: 1402,
        rebatePerLot: 2,
      },
      {
        key: "gold",
        rank: 3,
        tv: 89_700_000,
        defaultSetId: 1403,
        rebatePerLot: 3,
      },
      {
        key: "platinum",
        rank: 4,
        tv: 448_500_000,
        defaultSetId: 1404,
        rebatePerLot: 5,
      },
      {
        key: "brilliant",
        rank: 5,
        tv: 897_000_000,
        defaultSetId: 1405,
        rebatePerLot: 7,
      },
      {
        key: "elite",
        rank: 6,
        tv: 1_794_000_000,
        defaultSetId: 1406,
        rebatePerLot: 10,
      },
    ],
  },
  LATAM: {
    criteria: ["tv", "ac", "nc"],
    tiers: [
      {
        key: "bronze",
        rank: 3,
        tv: 0,
        ac: 1,
        nc: 0,
        defaultSetId: 1503,
        rebatePerLot: 3,
      },
      {
        key: "silver",
        rank: 4,
        tv: 250_000_000,
        ac: 3,
        nc: 10,
        defaultSetId: 1504,
        rebatePerLot: 5,
      },
      {
        key: "gold",
        rank: 5,
        tv: 500_000_000,
        ac: 10,
        nc: 20,
        defaultSetId: 1505,
        rebatePerLot: 7,
      },
      {
        key: "platinum",
        rank: 6,
        tv: 750_000_000,
        ac: 50,
        nc: 30,
        defaultSetId: 1506,
        rebatePerLot: 10,
      },
    ],
  },
  MENA: {
    criteria: ["tv"],
    tiers: [
      { key: "bronze", rank: 1, tv: 0, defaultSetId: 1601, rebatePerLot: 1 },
      {
        key: "silver",
        rank: 2,
        tv: 50_000_000,
        defaultSetId: 1602,
        rebatePerLot: 2,
      },
      {
        key: "gold",
        rank: 3,
        tv: 400_000_000,
        defaultSetId: 1603,
        rebatePerLot: 3,
      },
      {
        key: "platinum",
        rank: 4,
        tv: 800_000_000,
        defaultSetId: 1604,
        rebatePerLot: 5,
      },
    ],
  },
};

const toLadderFixture = ({
  criteria,
  tiers,
}: (typeof FIXTURE_LADDERS)[Region]): RebateTierLadderTransport => ({
  criteria,
  tiers: tiers.map(({ key, rank, tv, ac, nc, defaultSetId }) => ({
    key,
    rank,
    tv,
    ac,
    nc,
    rebateDefaultSetId: defaultSetId,
  })),
});

const toMaxRebateFixture = ({
  tiers,
}: (typeof FIXTURE_LADDERS)[Region]): MaxRebatePerLotTransport => ({
  currency: "USD",
  tiers: tiers.map(({ key, rank, defaultSetId, rebatePerLot }) => ({
    key,
    rank,
    isExclusive: false,
    maxRebatePerLot: rebatePerLot,
    rebateDefaultSetId: defaultSetId,
  })),
});

export const rebateTierLadderFixtures: Record<
  Region,
  RebateTierLadderTransport
> = {
  TH: toLadderFixture(FIXTURE_LADDERS.TH),
  VN: toLadderFixture(FIXTURE_LADDERS.VN),
  HK: toLadderFixture(FIXTURE_LADDERS.HK),
  REST: toLadderFixture(FIXTURE_LADDERS.REST),
  LATAM: toLadderFixture(FIXTURE_LADDERS.LATAM),
  MENA: toLadderFixture(FIXTURE_LADDERS.MENA),
};

export const defaultRebateTierLadderFixture: RebateTierLadderTransport =
  rebateTierLadderFixtures.REST;

export const maxRebatePerLotFixtures: Record<Region, MaxRebatePerLotTransport> =
  {
    TH: toMaxRebateFixture(FIXTURE_LADDERS.TH),
    VN: toMaxRebateFixture(FIXTURE_LADDERS.VN),
    HK: toMaxRebateFixture(FIXTURE_LADDERS.HK),
    REST: toMaxRebateFixture(FIXTURE_LADDERS.REST),
    LATAM: toMaxRebateFixture(FIXTURE_LADDERS.LATAM),
    MENA: toMaxRebateFixture(FIXTURE_LADDERS.MENA),
  };

export const defaultMaxRebatePerLotFixture: MaxRebatePerLotTransport =
  maxRebatePerLotFixtures.REST;

/** What `/ladder` answers when the caller's region cannot be resolved. */
export const unresolvedRebateTierLadderFixture: RebateTierLadderTransport = {
  criteria: [],
  tiers: [],
};

const notificationFixture = (
  type: string,
  overrides: Partial<LatestNotificationTransport> = {},
): LatestNotificationTransport => ({
  id: `notification-${type.toLowerCase()}`,
  type,
  domain: "rebate-tier",
  referenceId: "reference-001",
  referenceName: "partner_tier_id",
  title: type,
  message: null,
  metadata: null,
  dismissedAt: null,
  readAt: null,
  createdAt: FIXTURE_EVALUATED_AT,
  ...overrides,
});

export const latestNotificationFixtures = {
  tierUpgraded: notificationFixture("TIER_UPGRADED"),
  tierDowngraded: notificationFixture("TIER_DOWNGRADED"),
  tierAtRisk: notificationFixture("TIER_AT_RISK"),
  graceAssigned: notificationFixture("GP_ASSIGNED"),
  graceExtended: notificationFixture("GP_EXTENDED"),
  graceExpired: notificationFixture("GP_EXPIRED"),
  freezeAssigned: notificationFixture("TF_ASSIGNED"),
  freezeExtended: notificationFixture("TF_EXTENDED"),
  freezeUnassigned: notificationFixture("TF_UNASSIGNED"),
  freezeExpired: notificationFixture("FP_EXPIRED"),
  dismissed: notificationFixture("TIER_UPGRADED", {
    id: "notification-dismissed",
    dismissedAt: FIXTURE_EVALUATED_AT,
  }),
  none: null,
} satisfies Record<string, LatestNotificationTransport | null>;

export const rebateTierScenarioNotifications: Partial<
  Record<RebateTierScenario, LatestNotificationTransport>
> = {
  grace: latestNotificationFixtures.graceAssigned,
  qualificationCountdown: latestNotificationFixtures.tierAtRisk,
  frozen: latestNotificationFixtures.freezeAssigned,
  freezeActive: latestNotificationFixtures.freezeAssigned,
  freezeLapsed: latestNotificationFixtures.freezeUnassigned,
  tierUpgrade: latestNotificationFixtures.tierUpgraded,
  tierDowngrade: latestNotificationFixtures.tierDowngraded,
  graceWithTierEvent: latestNotificationFixtures.tierUpgraded,
};

type FixtureRebateShare = {
  symbol: string;
  standard: number;
  razor: number;
  islamic: number;
};

const FIXTURE_REBATE_SHARES: {
  asset: string;
  symbols: FixtureRebateShare[];
}[] = [
  {
    asset: "FX",
    symbols: [
      { symbol: "EURUSD", standard: 0.46, razor: 0.32, islamic: 0.4 },
      { symbol: "GBPUSD", standard: 0.42, razor: 0.29, islamic: 0.36 },
      { symbol: "AUDUSD", standard: 0.38, razor: 0.26, islamic: 0.32 },
      { symbol: "USDJPY", standard: 0.35, razor: 0.24, islamic: 0.3 },
    ],
  },
  {
    asset: "Commodity",
    symbols: [
      { symbol: "XAUUSD", standard: 0.37, razor: 0.26, islamic: 0.32 },
      { symbol: "XAGUSD", standard: 0.3, razor: 0.21, islamic: 0.26 },
      { symbol: "XTIUSD", standard: 0.24, razor: 0.17, islamic: 0.2 },
    ],
  },
  {
    asset: "CFD",
    symbols: [
      { symbol: "US500", standard: 0.19, razor: 0.13, islamic: 0.16 },
      { symbol: "NAS100", standard: 0.16, razor: 0.11, islamic: 0.14 },
      { symbol: "GER40", standard: 0.14, razor: 0.1, islamic: 0.12 },
    ],
  },
  {
    asset: "Equity",
    symbols: [
      { symbol: "AAPL", standard: 0.001, razor: 0.001, islamic: 0.001 },
      { symbol: "TSLA", standard: 0.001, razor: 0.001, islamic: 0.001 },
    ],
  },
  {
    asset: "Crypto",
    symbols: [
      { symbol: "BTCUSD", standard: 0.09, razor: 0.06, islamic: 0.08 },
      { symbol: "ETHUSD", standard: 0.07, razor: 0.05, islamic: 0.06 },
    ],
  },
  {
    asset: "Treasuries",
    symbols: [
      { symbol: "UST10Y", standard: 0, razor: 0, islamic: 0 },
      { symbol: "GILT10Y", standard: 0, razor: 0, islamic: 0 },
    ],
  },
  {
    asset: "SpreadBetting",
    symbols: [{ symbol: "UK100SB", standard: 0, razor: 0, islamic: 0 }],
  },
];

const toCents = (value: number): number => Math.round(value * 100) / 100;

/** Absolute per-lot rates, in AUD, for a tier paying up to `rebatePerLot`. */
export const rebateDetailsFixtureFor = (
  rebatePerLot: number,
): RebateDetailsTransport => ({
  currency: "AUD",
  assets: FIXTURE_REBATE_SHARES.map(
    ({ asset, symbols }): AssetRateTransport => {
      const rated = symbols.map((share) => ({
        symbol: share.symbol,
        standardValue: toCents(share.standard * rebatePerLot),
        razorValue: toCents(share.razor * rebatePerLot),
        islamicValue: toCents(share.islamic * rebatePerLot),
      }));
      return {
        asset,
        standardValue: Math.max(...rated.map((row) => row.standardValue)),
        razorValue: Math.max(...rated.map((row) => row.razorValue)),
        islamicValue: Math.max(...rated.map((row) => row.islamicValue)),
        symbols: rated,
      };
    },
  ),
});

/** `/rebate-details` responses keyed by the ladder fixtures' `defaultSetId`. */
export const rebateDetailsFixtures: Record<number, RebateDetailsTransport> =
  Object.fromEntries(
    Object.values(FIXTURE_LADDERS).flatMap((ladder) =>
      ladder.tiers.map((tier) => [
        tier.defaultSetId,
        rebateDetailsFixtureFor(tier.rebatePerLot),
      ]),
    ),
  );

/** 17:15 New York (EDT) on the Aug 13 trading day, as `/volume-units` returns it. */
export const FIXTURE_RATE_DATE = "2026-08-13T21:15:00Z";

export const volumeUnitsFixture: VolumeUnitsTransport = {
  options: [
    { symbol: "XAUUSD", perLotUsd: 374_029.5 },
    { symbol: "NAS100", perLotUsd: 23_987.4 },
    { symbol: "HK50", perLotUsd: 3_271.85 },
    { symbol: "EURUSD", perLotUsd: 117_979 },
    { symbol: "GBPUSD", perLotUsd: 134_211 },
    { symbol: "XAGUSD", perLotUsd: 218_926.75 },
    { symbol: "US30", perLotUsd: 45_102.6 },
    { symbol: "BTCUSD", perLotUsd: 111_708 },
    { symbol: "AUDUSD", perLotUsd: 65_842 },
    { symbol: "USDJPY", perLotUsd: 100_000 },
  ],
  rateDate: FIXTURE_RATE_DATE,
};
